import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Send, User, Mail, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SendEmailPage = () => {
  const { user } = useAuth();
  const [senders, setSenders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    recipient_email: '',
    recipient_first_name: '',
    sender_id: '',
    template_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sendersRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/senders`),
        axios.get(`${API_URL}/templates`)
      ]);
      setSenders(sendersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load senders and templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setFormData({ ...formData, template_id: templateId });
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
  };

  const getPreviewHtml = () => {
    if (!selectedTemplate) return '';
    const firstName = formData.recipient_first_name || 'Customer';
    return selectedTemplate.html_content.replace(/\{\{first_name\}\}/g, firstName);
  };

  const getPreviewSubject = () => {
    if (!selectedTemplate) return '';
    const firstName = formData.recipient_first_name || 'Customer';
    return selectedTemplate.subject.replace(/\{\{first_name\}\}/g, firstName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sender_id) {
      toast.error('Please select a sender');
      return;
    }
    if (!formData.template_id) {
      toast.error('Please select a template');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_URL}/emails/send`, formData);
      toast.success('Email sent successfully!');
      setFormData({
        recipient_email: '',
        recipient_first_name: '',
        sender_id: formData.sender_id,
        template_id: formData.template_id
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const selectedSender = senders.find(s => s.id === formData.sender_id);

  return (
    <div data-testid="send-email-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Send Email
        </h1>
        <p className="text-slate-500 mt-1">Compose and send personalized emails to your customers</p>
      </div>

      {senders.length === 0 || templates.length === 0 ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">Setup Required</h3>
                <p className="text-amber-700 mt-1">
                  {senders.length === 0 && 'No senders configured. '}
                  {templates.length === 0 && 'No templates created. '}
                  Please contact your administrator or manager to set these up.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Send className="w-5 h-5 text-primary" />
                Email Details
              </CardTitle>
              <CardDescription>Fill in the recipient information and select your options</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Recipient Email */}
                <div className="space-y-2">
                  <Label htmlFor="recipient_email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Recipient Email
                  </Label>
                  <Input
                    id="recipient_email"
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    placeholder="customer@example.com"
                    required
                    className="h-12"
                    data-testid="recipient-email-input"
                  />
                </div>

                {/* Recipient First Name */}
                <div className="space-y-2">
                  <Label htmlFor="recipient_first_name" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    Recipient First Name
                  </Label>
                  <Input
                    id="recipient_first_name"
                    type="text"
                    value={formData.recipient_first_name}
                    onChange={(e) => setFormData({ ...formData, recipient_first_name: e.target.value })}
                    placeholder="John"
                    required
                    className="h-12"
                    data-testid="recipient-name-input"
                  />
                  <p className="text-xs text-slate-500">
                    This will replace {'{{first_name}}'} in the template
                  </p>
                </div>

                {/* Sender Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Select Sender
                  </Label>
                  <Select
                    value={formData.sender_id}
                    onValueChange={(value) => setFormData({ ...formData, sender_id: value })}
                  >
                    <SelectTrigger className="h-12" data-testid="sender-select">
                      <SelectValue placeholder="Choose a sender email" />
                    </SelectTrigger>
                    <SelectContent>
                      {senders.map((sender) => (
                        <SelectItem key={sender.id} value={sender.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{sender.name}</span>
                            <span className="text-sm text-slate-500">{sender.email}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Template Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    Select Template
                  </Label>
                  <Select
                    value={formData.template_id}
                    onValueChange={handleTemplateChange}
                  >
                    <SelectTrigger className="h-12" data-testid="template-select">
                      <SelectValue placeholder="Choose an email template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-sm text-slate-500">{template.description || template.subject}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold"
                  disabled={sending || !formData.sender_id || !formData.template_id}
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Email
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Preview */}
          <div className="space-y-6">
            {/* Email Details Preview */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Email Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">From</span>
                  <span className="font-medium text-slate-900">
                    {selectedSender ? `${selectedSender.name} <${selectedSender.email}>` : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">To</span>
                  <span className="font-medium text-slate-900">
                    {formData.recipient_email || '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Subject</span>
                  <span className="font-medium text-slate-900">
                    {getPreviewSubject() || '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-slate-500">Template</span>
                  <span className="font-medium text-slate-900">
                    {selectedTemplate?.name || '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* HTML Preview */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileText className="w-5 h-5 text-violet-500" />
                  Template Preview
                </CardTitle>
                <CardDescription>
                  Preview how your email will look
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedTemplate ? (
                  <div className="border rounded-lg bg-white overflow-hidden">
                    <iframe
                      srcDoc={getPreviewHtml()}
                      title="Email Preview"
                      className="w-full h-80 border-0"
                      sandbox="allow-same-origin"
                      data-testid="email-preview"
                    />
                  </div>
                ) : (
                  <div className="border rounded-lg bg-slate-50 h-80 flex items-center justify-center">
                    <div className="text-center text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Select a template to see preview</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendEmailPage;
